import React, { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { Modal, Button, Form, Text, DeemphasizedLink } from '@fielded/shared-ui'

import { pluralize } from '../../../../../van-shared/utils/utils'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { CheckmarkCircle } from '@fielded/shared-ui/src/icons'

const VERIFICATION_STEPS = {
  CONFIRM: 'confirm',
  VERIFY: 'verify',
  FINISHED: 'finished'
}

const VerificationModal = ({
  isOpen,
  verificationType,
  contactInfo,
  isLoading,
  onSendCode,
  onVerifyCode,
  onClose
}) => {
  const [step, setStep] = useState(VERIFICATION_STEPS.CONFIRM)
  const [otpCode, setOtpCode] = useState('')
  const [resendTimer, setResendTimer] = useState(0)
  const [otpError, setOtpError] = useState('')

  const isFinishedStep = step === VERIFICATION_STEPS.FINISHED
  const isEmail = verificationType === 'email'
  const title = `Verify ${isEmail ? 'email' : 'phone number'}`

  const handleSendCode = async () => {
    try {
      await onSendCode(verificationType, contactInfo)
      setStep(VERIFICATION_STEPS.VERIFY)
      setResendTimer(60)
    } catch (error) {
      console.error('Failed to send verification code:', error)
    }
  }

  useEffect(() => {
    let interval
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1)
      }, 1000)
    }
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [resendTimer])

  const handleVerifyCode = async () => {
    try {
      await onVerifyCode(verificationType, otpCode, contactInfo)
      setStep(VERIFICATION_STEPS.FINISHED)
    } catch (error) {
      console.error('Failed to verify code:', error)
      setOtpError(error.message || 'Invalid code. Please check and try again.')
    }
  }

  const handleOtpChange = (value) => {
    if (otpError) {
      setOtpError('')
    }
    setOtpCode(value)
  }

  const handleClose = () => {
    setStep(VERIFICATION_STEPS.CONFIRM)
    setOtpCode('')
    onClose()
  }

  const renderConfirmStep = () => (
    <Fragment>
      <section className='verification-modal__content'>
        <Text>
          We'll send a 6-digit verification code to:
        </Text>
        <Text className='verification-modal__contact-info' bold>{contactInfo}</Text>
        <Text size='small' className='verification-modal__help-text'>
          Make sure you have access to this {verificationType} to receive the code.
        </Text>
      </section>
    </Fragment>
  )

  const renderVerifyStep = () => (
    <section className='verification-modal__content'>
      <Text>
          Enter the 6-digit code we sent to your {verificationType}
      </Text>

      <Form>
        <Form.Row className='verification-modal__otp-form-row'>
          <Form.Field labelText='' fieldName='otpCode'>
            <Form.Field.OTPInput
              hasError={otpError}
              onValueChange={handleOtpChange}
            />
            {otpError && (
              <Text className='verification-modal__otp-failed-message'>
                {otpError}
              </Text>
            )}
            <Form.Field.ValidationErrors />
          </Form.Field>
        </Form.Row>
      </Form>

      <div>
        <Text size='small' className='verification-modal__help-text'>
          {resendTimer ? (
            <Fragment>
                Resend code in {resendTimer} {pluralize('sec', resendTimer)}
            </Fragment>
          ) : (
            <Fragment>
              Didn’t receive code? <DeemphasizedLink
                colorVariant='brand'
                size='small'
                onClick={handleSendCode}
                bold
              >
                Resend
              </DeemphasizedLink>
            </Fragment>
          )}
        </Text>
      </div>
    </section>
  )

  const renderFinishedStep = () => (
    <section className='verification-modal__content-verified'>
      <Text className='verification-modal__content-verified-icon'>
        <CheckmarkCircle />
      </Text>

      <section className='verification-modal__content-verified-info-wrapper'>
        <Text bold>{isEmail ? 'Email' : 'Pnone number'} verified</Text>
        <Text className='verification-modal__content-verified-info'>
          Your {isEmail ? 'email address' : 'phone number'}  has been successfully verified.
        </Text>
      </section>
    </section>
  )

  let content = renderConfirmStep()
  let primaryButtonOnClick = handleSendCode
  let primaryButtonIsDisabled = false
  let primaryButtonText = 'Send code'
  let primaryButtonLoadingLabel = 'Sending...'

  if (step === VERIFICATION_STEPS.VERIFY) {
    content = renderVerifyStep()
    primaryButtonOnClick = handleVerifyCode
    primaryButtonIsDisabled = otpCode.length !== 6 || isLoading
    primaryButtonText = 'Verify'
    primaryButtonLoadingLabel = 'Verifying...'
  }

  if (isFinishedStep) {
    content = renderFinishedStep()
    primaryButtonOnClick = handleClose
    primaryButtonText = 'Done'
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      centered={false}
    >
      <section className='verification-modal__content-wrapper'>
        <SwitchTransition mode='out-in'>
          <CSSTransition
            timeout={300}
            classNames='slide'
            key={step}
          >
            <section className='verification-modal__content-info left'>
              {content}
            </section>
          </CSSTransition>
        </SwitchTransition>

        <section className='verification-modal__button-wrapper'>
          {!isFinishedStep && (
            <DeemphasizedLink onClick={handleClose}>
              Cancel
            </DeemphasizedLink>
          )}
          <Button
            onClick={primaryButtonOnClick}
            colorVariant='brand'
            loading={isLoading}
            loadingLabel={primaryButtonLoadingLabel}
            disabled={primaryButtonIsDisabled}
            fill='full'
          >
            {primaryButtonText}
          </Button>
        </section>
      </section>
    </Modal>
  )
}

VerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  verificationType: PropTypes.oneOf(['email', 'phone']).isRequired,
  contactInfo: PropTypes.string.isRequired,
  onSendCode: PropTypes.func.isRequired,
  onVerifyCode: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
}

VerificationModal.defaultProps = {
  isLoading: false,
  verificationType: 'email'
}

export default VerificationModal
